import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { THEMES, hash, rc } from "@/lib/damesBoard";
import { BoardDefs, DamesPiece } from "@/components/DamesPiece";

const CELL = 60;
const PAD = 22;
const SIZE = CELL * 10 + PAD * 2;

const useCenter = (flipped) => (sq) => {
  let [r, c] = rc(sq);
  if (flipped) { r = 9 - r; c = 9 - c; }
  return [PAD + c * CELL + CELL / 2, PAD + r * CELL + CELL / 2];
};

// Animates the last move hop by hop, then fades the captured pieces together.
const useMoveAnim = (board, last, animKey) => {
  const prev = useRef(board);
  const [anim, setAnim] = useState(null);
  useLayoutEffect(() => {
    const before = prev.current;
    prev.current = board;
    if (!last || !animKey || !before[last.path[0]]) { setAnim(null); return undefined; }
    const timers = [];
    setAnim({ before, path: last.path, captured: last.captured, piece: before[last.path[0]], step: 0, fade: false });
    last.path.slice(1).forEach((_, i) => timers.push(setTimeout(() => setAnim((a) => a && { ...a, step: i + 1 }), 30 + i * 260)));
    const end = 30 + (last.path.length - 1) * 260;
    if (last.captured.length) timers.push(setTimeout(() => setAnim((a) => a && { ...a, fade: true }), end));
    timers.push(setTimeout(() => setAnim(null), end + (last.captured.length ? 300 : 0)));
    return () => timers.forEach(clearTimeout);
  }, [animKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return anim;
};

const Squares = ({ theme, coords, center }) => {
  const cells = [];
  for (let r = 0; r < 10; r++) {
    for (let c = 0; c < 10; c++) {
      const dark = (r + c) % 2 === 1;
      const x = PAD + c * CELL, y = PAD + r * CELL;
      const h = hash(r * 10 + c + 1);
      cells.push(<rect key={`${r}-${c}`} x={x} y={y} width={CELL} height={CELL} fill={dark ? theme.dark : theme.light} />);
      cells.push(<rect key={`v${r}-${c}`} x={x} y={y} width={CELL} height={CELL} fill={h > 0.5 ? "#fff" : "#000"} opacity={0.02 + Math.abs(h - 0.5) * 0.09} />);
    }
  }
  return (
    <g>
      {cells}
      <rect x={PAD} y={PAD} width={CELL * 10} height={CELL * 10} filter="url(#dm-grain)" opacity={theme.grain} style={{ mixBlendMode: "multiply" }} />
      {coords && Array.from({ length: 50 }, (_, i) => {
        const [x, y] = center(i + 1);
        return <text key={i} x={x - CELL / 2 + 4} y={y - CELL / 2 + 12} fontSize="10" fill="#fff" opacity="0.45" style={{ fontFamily: "monospace" }}>{i + 1}</text>;
      })}
    </g>
  );
};

export const DamesBoard = ({ board, legal = [], onMove, flipped = false, themeKey = "bois", coords = true, last, animKey, disabled }) => {
  const theme = THEMES[themeKey] || THEMES.bois;
  const center = useCenter(flipped);
  const svg = useRef(null);
  const [sel, setSel] = useState(null);
  const [preview, setPreview] = useState(null);
  const [drag, setDrag] = useState(null);
  const anim = useMoveAnim(board, last, animKey);
  useEffect(() => { setSel(null); setPreview(null); }, [animKey]);

  const origins = useMemo(() => new Set(legal.map((m) => m.path[0])), [legal]);
  const dests = useMemo(() => {
    const d = {};
    legal.filter((m) => m.path[0] === sel).forEach((m) => { d[m.path[m.path.length - 1]] ??= m; });
    return d;
  }, [legal, sel]);

  const toSvg = (e) => {
    const p = svg.current.createSVGPoint();
    p.x = e.clientX; p.y = e.clientY;
    return p.matrixTransform(svg.current.getScreenCTM().inverse());
  };
  const sqAt = ({ x, y }) => {
    for (let s = 1; s <= 50; s++) {
      const [cx, cy] = center(s);
      if (Math.abs(cx - x) < CELL / 2 && Math.abs(cy - y) < CELL / 2) return s;
    }
    return null;
  };
  const play = (m) => { setSel(null); setPreview(null); onMove?.(m.path); };
  const tapSquare = (s) => {
    if (disabled || anim) return;
    if (s && origins.has(s)) { setSel(s === sel ? null : s); setPreview(null); return; }
    const m = s && dests[s];
    if (!m) { setSel(null); setPreview(null); return; }
    if (m.captured.length && preview?.path.join("-") !== m.path.join("-")) { setPreview(m); return; }
    play(m);
  };
  const onDown = (e) => {
    if (disabled || anim) return;
    const pt = toSvg(e), s = sqAt(pt);
    if (s && origins.has(s)) {
      try { svg.current.setPointerCapture(e.pointerId); } catch { /* synthetic pointer */ }
      setSel(s); setPreview(null);
      setDrag({ from: s, x: pt.x, y: pt.y, moved: false, wasSel: s === sel });
    }
  };
  const onMoveP = (e) => {
    if (!drag) return;
    const pt = toSvg(e);
    const moved = drag.moved || Math.hypot(pt.x - center(drag.from)[0], pt.y - center(drag.from)[1]) > 12;
    setDrag({ ...drag, x: pt.x, y: pt.y, moved });
    const over = sqAt(pt);
    setPreview(moved && over && dests[over]?.captured.length ? dests[over] : null);
  };
  const onUp = (e) => {
    const pt = toSvg(e), s = sqAt(pt);
    if (drag?.moved) {
      setDrag(null);
      if (s && dests[s]) play(dests[s]);
      return;
    }
    setDrag(null);
    if (drag?.wasSel) setSel(null);
    if (!drag) tapSquare(s);
  };

  const shown = anim ? anim.before : board;
  const fadedSet = new Set((preview?.captured) || []);
  const pieces = Object.entries(shown).map(([k, p]) => {
    const s = Number(k);
    if (anim && s === anim.path[0]) return null;
    if (drag?.moved && drag.from === s) return null;
    const [x, y] = center(s);
    return <DamesPiece key={`p${s}`} id={`dames-piece-${s}`} piece={p} x={x} y={y} theme={theme} lifted={sel === s}
      faded={fadedSet.has(s)} fading={anim?.fade && anim.captured.includes(s)} pop={!anim && last?.promoted && s === last.path[last.path.length - 1]} />;
  });
  const lastSq = last && !anim ? [last.path[0], last.path[last.path.length - 1]] : [];
  return (
    <svg ref={svg} viewBox={`0 0 ${SIZE} ${SIZE}`} className="block h-auto w-full touch-none select-none" data-testid="dames-board"
      onPointerDown={onDown} onPointerMove={onMoveP} onPointerUp={onUp} shapeRendering="geometricPrecision">
      <BoardDefs theme={theme} />
      <rect x="2" y="2" width={SIZE - 4} height={SIZE - 4} rx="22" fill={theme.frame} filter="url(#dm-frame-shadow)" />
      <rect x={PAD - 3} y={PAD - 3} width={CELL * 10 + 6} height={CELL * 10 + 6} rx="4" fill="none" stroke="#000" strokeOpacity="0.35" strokeWidth="3" />
      <Squares theme={theme} coords={coords} center={center} />
      {lastSq.map((s, i) => { const [x, y] = center(s); return <rect key={`l${i}`} pointerEvents="none" x={x - CELL / 2} y={y - CELL / 2} width={CELL} height={CELL} fill="#F5C542" opacity="0.32" data-testid={`dames-last-${i}`} />; })}
      {preview && <polyline pointerEvents="none" points={preview.path.map((s) => center(s).join(",")).join(" ")} fill="none" stroke="#FFE7A3" strokeWidth="4" strokeOpacity="0.75" strokeDasharray="2 8" strokeLinecap="round" data-testid="dames-preview-path" />}
      {pieces}
      {drag?.moved && shown[drag.from] && <DamesPiece id={`dames-piece-${drag.from}`} piece={shown[drag.from]} x={drag.x} y={drag.y} theme={theme} lifted noTransition />}
      {anim && (() => { const [x, y] = center(anim.path[anim.step]); return <DamesPiece id="dames-moving" piece={anim.piece} x={x} y={y} theme={theme} hop={anim.step} lifted />; })()}
      {Object.entries(dests).map(([k, m]) => {
        const [x, y] = center(Number(k));
        return m.captured.length
          ? <circle key={`d${k}`} cx={x} cy={y} r="20" fill="none" stroke="#FFE7A3" strokeWidth="4" opacity="0.85" data-testid={`dames-dest-${k}`} />
          : <circle key={`d${k}`} cx={x} cy={y} r="9" fill="#FFE7A3" opacity="0.7" data-testid={`dames-dest-${k}`} />;
      })}
    </svg>
  );
};
